import { KeyboardEvent } from 'react';
import { connect } from 'react-redux';
import { RootPatronState } from '../../common/use-patron-selector';
import { UserSuggestion } from '../../common/use-user-suggestions';
import { advocateSelectors } from '../../models/advocate';
import MentionPickerListItem from './MentionPickerListItem';

type StateProps = ReturnType<typeof mapsStateToProps>;

type OwnProps = {
  users: UserSuggestion[];
  listboxId: string;
  onClick: (mention: string, advocateId?: string) => void;
  onHover: (index: number) => void;
  selected: number | undefined;
};

type MentionPickerListProps = StateProps & OwnProps;

const MentionPickerList = ({
  users,
  listboxId,
  onClick,
  onHover,
  selected,
  advocateId,
}: MentionPickerListProps) => {
  const selectedUser = selected !== undefined ? users[selected] : undefined;

  const userList = users.map((user, index) => (
    <MentionPickerListItem
      id={`${listboxId}-option-${user.id}`}
      user={user}
      isSelf={advocateId === parseInt(user.id, 10)}
      onClick={onClick}
      onHover={() => onHover(index)}
      selected={selectedUser?.id === user.id}
      key={user.id}
    />
  ));

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // A suggestion row (MentionMenuItem) is independently focusable and
    // already handled Enter/Space itself by calling onSelect. MentionPicker
    // also listens for Enter/Tab on `document` to commit whichever row its
    // own arrow-key cursor points at. Stop the event here, once it has
    // already bubbled past the focused row, so a Tab-focused row doesn't
    // insert its mention twice.
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.stopPropagation();
    }
  };

  return (
    <div
      id={listboxId}
      className="mention-picker__list"
      role="listbox"
      aria-label="User suggestions"
      // Not tab-reachable: each row is independently focusable and
      // MentionPicker itself drives selection with arrow keys (mirrors the
      // Lexical body editor's dropdown).
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {userList.length ? userList : null}
    </div>
  );
};

const mapsStateToProps = (state: RootPatronState) => ({
  advocateId: advocateSelectors.getAdvocateId(state),
});

export default connect(mapsStateToProps)(MentionPickerList);
